/**
 * Disclosure Service — Firestore-backed acknowledgments for compliance
 * disclaimers across the app.
 *
 * Why Firestore instead of localStorage:
 *   - Server-side audit trail of who acknowledged what, when.
 *   - Survives device changes, browser cache wipes, incognito mode.
 *   - Lets us pin the disclosure to a specific VERSION; bumping the
 *     version forces every user to re-acknowledge after a material
 *     copy change.
 *
 * Storage shape:
 *   users/{uid}/settings/{kind}DisclosureAck
 *     {
 *       acknowledgedAt: serverTimestamp,
 *       version:        "v1",
 *       sections:       ["intro", "is", "isnot", "risk", "past", "yours"],
 *       userAgent:      "..."  (optional — included for audit context)
 *     }
 *
 * Currently used by:
 *   - "riskAssessment" disclosure gate (was localStorage, now Firestore)
 *   - "research"       disclosure gate (new in Research feature)
 */

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../authentication/firebase-config";

/* ─── Versioning per disclosure kind ─────────────────────────────── */

// Bump these when the disclosure copy materially changes. All existing
// acknowledgments become stale and users will see the gate again.
export const DISCLOSURE_VERSIONS = {
  riskAssessment: "v1",
  research:       "v1",
};

const docPath = (uid, kind) =>
  doc(db, "users", uid, "settings", `${kind}DisclosureAck`);

/* ─── Read ───────────────────────────────────────────────────────── */

/**
 * Fetch a user's acknowledgment for a given disclosure kind. Returns
 * the record if present AND the version matches; null otherwise.
 *
 * Returning null in either case means "show the gate."
 */
export async function fetchDisclosureAck(uid, kind) {
  if (!uid || !kind) return null;
  const expectedVersion = DISCLOSURE_VERSIONS[kind];
  if (!expectedVersion) return null;
  try {
    const snap = await getDoc(docPath(uid, kind));
    if (!snap.exists()) return null;
    const data = snap.data();
    if (data.version !== expectedVersion) return null; // stale → re-ack
    return data;
  } catch (_) {
    // Offline / permission error → fail closed (show gate)
    return null;
  }
}

/* ─── Write ──────────────────────────────────────────────────────── */

/**
 * Persist a user's acknowledgment. Idempotent — overwrites any prior
 * record for this kind. Includes basic audit context (userAgent).
 */
export async function writeDisclosureAck(uid, kind, sections) {
  if (!uid) throw new Error("uid required");
  const version = DISCLOSURE_VERSIONS[kind];
  if (!version) throw new Error(`Unknown disclosure kind: ${kind}`);

  const ua =
    typeof navigator !== "undefined" && navigator.userAgent
      ? navigator.userAgent.slice(0, 200)
      : "";

  const record = {
    acknowledgedAt: serverTimestamp(),
    version,
    sections,
    userAgent: ua,
  };
  await setDoc(docPath(uid, kind), record, { merge: false });
  return record;
}

/* ─── Convenience: current user ──────────────────────────────────── */

export function getCurrentUid() {
  try {
    return getAuth().currentUser?.uid || null;
  } catch (_) {
    return null;
  }
}

/* ─── ARC-15: categorizer disclosure ─────────────────────────────── */
/**
 * The AI Statement Categorizer sends a member to a third-party AI service to
 * upload their own bank statement. ARCrypto never receives the file, but we
 * are the ones pointing them there, so the disclosure is acknowledged before
 * first use.
 *
 * This kind deliberately does NOT use DISCLOSURE_VERSIONS above: the version
 * constant lives next to the copy it pins (CATEGORIZER_DISCLOSURE_VERSION in
 * components/expenses/CategorizerDisclosureModal.js) and is passed in, so the
 * wording and the version can never drift apart across two files.
 *
 * Storage shape — field names are fixed by ARC-15:
 *   users/{uid}/settings/categorizerDisclosureAck
 *     {
 *       categorizerDisclosureAcceptedVersion: "1.0",
 *       categorizerDisclosureAcceptedAt:      "2026-08-04T12:00:00.000Z"
 *     }
 *
 * acceptedAt is a plain ISO string rather than serverTimestamp() because the
 * client reads it straight back; nothing here needs server-authoritative time.
 */

const categorizerDocPath = (uid) =>
  doc(db, "users", uid, "settings", "categorizerDisclosureAck");

/**
 * The raw stored acknowledgment, or null when there is none. Fails closed —
 * offline or permission errors resolve to null, which means "show the gate".
 */
export async function fetchCategorizerDisclosureAck(uid) {
  if (!uid) return null;
  try {
    const snap = await getDoc(categorizerDocPath(uid));
    if (!snap.exists()) return null;
    return snap.data() || null;
  } catch (_) {
    return null;
  }
}

/**
 * Persist an acknowledgment of `version`. Throws if it could not be written so
 * the caller can keep the gate up instead of claiming a save that never landed.
 */
export async function writeCategorizerDisclosureAck(uid, version) {
  if (!uid) throw new Error("uid required");
  if (!version) throw new Error("version required");

  const record = {
    categorizerDisclosureAcceptedVersion: String(version),
    categorizerDisclosureAcceptedAt: new Date().toISOString(),
  };
  await setDoc(categorizerDocPath(uid), record, { merge: true });
  return record;
}
